import { defineStore } from 'pinia';
import api from '@/api';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    isLoading: true,
    profileSynced: false
  }),

  getters: {
    currentUser: (state) => state.user,
    isAuthenticated: (state) => !!state.user && !state.user?.expired,
    userProfile: (state) => state.user?.profile || null,
    accessToken: (state) => state.user?.access_token || null
  },

  actions: {
    // Returns the syncProfile() promise so a caller that needs permissions
    // to be correct before proceeding (the OIDC callback, the nav guard)
    // can await it - a brand-new registration's role only exists after
    // syncProfile's backend call provisions it, and permissionsStore.fetch()
    // only ever runs its real request once per session (guarded by
    // `loaded`), so a caller that fetches permissions without waiting for
    // provisioning first can permanently strand an empty permission set -
    // confirmed live as "menu is empty until the page is refreshed" for a
    // just-registered user. Callers that don't need to wait (token-refresh
    // call sites, where the user is already long since provisioned) can
    // still just not await it, same as before.
    setUser(user) {
      this.user = user;
      this.isLoading = false;
      return this.syncProfile();
    },

    clearUser() {
      this.user = null;
      this.isLoading = false;
      this.profileSynced = false;

      import('@/store/permissions').then(({ usePermissionsStore }) => usePermissionsStore().clear());
    },

    setLoading(loading) {
      this.isLoading = loading;
    },

    /**
     * Upserts this user's row in the operational backend (hr.t_user) so they
     * show up in operator/station assignment lists. Only invited users are
     * missing this row - it's never created there otherwise. Fire-and-forget,
     * once per session.
     */
    async syncProfile() {
      if (this.profileSynced || !this.user?.profile) return;
      this.profileSynced = true;
      try {
        await api.user.syncProfile({
          email: this.user.profile.email,
          displayName: this.user.profile.name,
          orgName: this.user.profile.orgName
        });

        // Import here (not top-level) to avoid a circular store dependency.
        const { usePermissionsStore } = await import('@/store/permissions');
        await usePermissionsStore().fetch();
      } catch (e) {
        this.profileSynced = false;
      }
    }
  }
});
